/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  BUSINESS_PLAN,
  DISMISS_MSG,
  FCORG_BLACKLIST,
  GMTVP,
  NON_TVP_FCORGS,
  TVP,
  VIEW_AUTHORITY
} from '../../../common/constants';
import {
  IndustryScenarioSelectPopUpComponent
} from '../../../core/components/industry-scenario-select/industry-scenario-select-pop-up/industry-scenario-select-pop-up.component';
import {
  IndustryScenarioSelectComponent
} from '../../../core/components/industry-scenario-select/industry-scenario-select.component';
import {InfoPopUpComponent} from '../../../core/components/pop-ups/info-pop-up/info-pop-up.component';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {StateService} from '../../../core/state/state.service';
import {EngProgClass} from '../../../models/eng-prog-class.model';
import {ForecastState} from '../../../models/forecast-state.model';
import {IndustryScenario} from '../../../models/industry-scenario-dto.model';
import {PropulsionType} from '../../../models/propulsion-type.model';
import {TvpVehicleProgram} from '../../../models/tvp-vehicle-program.model';
import {VehicleScenario} from '../../../models/vehicle-scenario-dto.model';
import {ExcelExportService} from '../../../services/excel-export.service';
import {VehicleScenarioService} from '../../../services/vehicle-scenario.service';
import {
  SelectVehicleProgramPopUpComponent
} from './select-vehicle-program-pop-up/select-vehicle-program-pop-up.component';


export const VEHICLE_SCENARIO_PATH = '/vehicle-scenario';
export const SCEN_NAME_MAX_LENGTH = 35;
export const ASSM_MAX_LENGTH = 150;

@Component({
    selector: 'app-create-vehicle-scenario',
    templateUrl: './create-vehicle-scenario.component.html',
    styleUrls: ['./create-vehicle-scenario.component.scss'],
    standalone: false
})


export class CreateVehicleScenarioComponent implements OnInit {
  protected readonly VIEW_AUTHORITY = VIEW_AUTHORITY;
  protected readonly NON_TVP_FCORGS = NON_TVP_FCORGS;
  protected readonly GMTVP = GMTVP;
  @ViewChild('industryScenarioSelect') industryScenarioSelect: IndustryScenarioSelectComponent;


  // Page Manipulation and Constants
  readonly loading = new LoadingCounter();
  user: string;
  stepTwoComplete: boolean;
  scenarioNameMaxLength = SCEN_NAME_MAX_LENGTH;
  assmMaxLength = ASSM_MAX_LENGTH;
  isTvp: boolean;
  fromScenario = 'From Existing Scenario';
  fromPortfolio = 'From Portfolio';
  fromTemplate = 'From Template';

  // First Page
  scenCatList: string[];
  scenTypeList: string[];
  propTypeList: PropulsionType[];
  engProgClassList: EngProgClass[];
  fcStateList: ForecastState[];
  scenarioName: string;
  scenCat: string;
  scenType: string;
  propulsionType: PropulsionType;
  engProgClass: EngProgClass;
  forecastState: ForecastState;
  assm: string;

  // Second Page
  sourceList: { id: number; value: string, tvp: boolean }[] = [
    {id: 0, value: this.fromScenario, tvp: true},
    {id: 1, value: this.fromPortfolio, tvp: true},
    {id: 2, value: this.fromTemplate, tvp: false}
  ];
  selectedSource: any;

  sharedVolList: { id: number; value: string }[] = [
    {id: 0, value: 'Keep GM Volume the Same'},
    {id: 1, value: 'Keep Market Share'}];
  keepSharedVol: number;
  selectedScenario: VehicleScenario;

  // By Portfolio
  portfolioFcorgs: string[];
  selectedFcorg: string;
  architectures: string[];
  selectedArchitecture: string;
  selectedVehiclePrograms: TvpVehicleProgram[];

  // Industry Selection
  selectedIndustryScenario: IndustryScenario;

  // By Template
  file: File;
  sheets: string[];
  selectedSheet: string;
  templateFcorg: string;

  constructor(public snackBar: FabMessageService,
              public stateService: StateService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              public readonly excelService: ExcelExportService,
              readonly router: Router,
              public readonly dialog: MatDialog) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  ngOnInit(): void {
    this.initializeVariables();
  }

  /**
   * Sets initial values for all variables
   */
  initializeVariables(): void {
    this.isTvp = false;
    this.selectedSource = -1;
    this.keepSharedVol = null;
    this.stepTwoComplete = false;
    this.user = this.stateService.getUserGmid();
    this.getScenarioCategories();
    this.getScenarioTypes();
    this.getPropTypes();
    this.getEngProgClass();
    this.getFcStates();
  }

  /**
   * Calls service function to get all scenario types
   */
  getScenarioTypes(): void {
    this.loading.track(this.vehicleScenarioService.getAllScenarioTypes()).subscribe(
      data => {
        this.scenTypeList = data.map(entry => entry.SCEN_TYPE);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      });
  }

  /**
   * Calls service function to get all scenario categories
   */
  getScenarioCategories(): void {
    this.loading.track(this.vehicleScenarioService.getAllScenarioCategories()).subscribe(
      data => {
        this.scenCatList = data.map(entry => entry.scenarioCategory);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Gets the propulsion types for creating a TVP scenario
   */
  getPropTypes(): void {
    this.loading.track(this.vehicleScenarioService.getAllPropulsionTypes()).subscribe(
      data => {
        this.propTypeList = data;
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Gets the engineering program classes for creating a TVP scenario
   */
  getEngProgClass(): void {
    this.loading.track(this.vehicleScenarioService.getAllEngProgClass()).subscribe(
      data => {
        this.engProgClassList = data;
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Gets the forecast states for creating a TVP scenario
   */
  getFcStates(): void {
    this.loading.track(this.vehicleScenarioService.getAllForecastStates()).subscribe(
      data => {
        this.fcStateList = data;
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Calls service function to get all fcorgs that are used in scenarios for portfolio dropdown
   */
  getPortFcorgs(user): void {
    this.loading.track(this.vehicleScenarioService.getFcorgsForPortfolio(user)).subscribe(
      data => {
        this.portfolioFcorgs = data.filter((fcorg: string) => !FCORG_BLACKLIST.includes(fcorg));
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Gets the architectures for creating a TVP scenario
   */
  getArchitectures(): void {
    this.loading.track(this.vehicleScenarioService.getTvpArchitectures()).subscribe(
      data => {
        this.architectures = data.map(entry => entry.arch);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * When the TVP status changes, resets all selections, the tables, the industry scenario status,
   * and updates step 2 complete
   */
  onTvpChange(): void {
    this.resetSecondPage();
    this.selectedSource = null;
    this.updateStepTwoComplete();
    this.scenCatList.reverse();
  }

  /**
   * Called when the base scenario source dropdown is changed
   * resets industry values, resets selections, updates completion status, and fetches data based on source
   */
  onSourceChange(): void {
    this.resetSecondPage();
    this.updateStepTwoComplete();
    if (this.selectedSource.value === this.fromPortfolio) {
      if (this.isTvp) {
        this.getArchitectures();
      } else {
        this.getPortFcorgs(this.user);
      }
    }
  }

  /**
   * Selects a vehicle scenario for use as base, then resets the industry values and gets the industry scenarios
   * @param event the event with the selected scenario
   */
  onSelectVehicleScenario(event): void {
    if (this.selectedScenario == event.selectedScenarios[0]) {
      return;
    }
    this.selectedScenario = event.selectedScenarios[0];
    this.selectedIndustryScenario = null;
    this.updateStepTwoComplete();
    if (this.selectedScenario) {
      this.openIndSelectComponent();
    }
  }

  /**
   * Opens the pop up to select the vehicle programs
   * @param architecture the architecture to filter the vehicle programs by
   */
  openProgramList(architecture: string): void {
    this.dialog.open(SelectVehicleProgramPopUpComponent, {
      width: '75%',
      data: {
        architecture
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.selectedVehiclePrograms = result.data.programs;
        this.selectedFcorg = GMTVP;
        this.openIndSelectComponent();
      }
    });
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.selectedSheet = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        if (this.selectedSheet.length === 0) {
          this.file = null;
        }
        this.getTemplateFcorg(this.file, this.selectedSheet);
      }
    );
  }

  /**
   * Calls the service function to submit the template file and get the fcorg
   * @param file the uploaded Excel file
   * @param sheetName the name of the sheet to use
   */
  getTemplateFcorg(file: File, sheetName: string): void {
    this.loading.track(this.vehicleScenarioService.getFcorgsFromTemplate(file, sheetName)).subscribe(
      (data) => {
        this.templateFcorg = data[0];
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
        this.file = null;
      },
      () => {
        this.openIndSelectComponent()
      }
    );
  }


  /**
   * Opens the pop-up to select an industry scenario
   */
  openIndSelectComponent(): void {
    let industryFcorg = '';
    switch (this.selectedSource.value) {
      case this.fromScenario:
        industryFcorg = this.translateFcorg(this.selectedScenario.fcorg);
        break;
      case this.fromPortfolio:
        industryFcorg = this.translateFcorg(this.selectedFcorg);
        break;
      case this.fromTemplate:
        industryFcorg = this.translateFcorg(this.templateFcorg);
        break;
    }

    const dialogRef = this.dialog.open(IndustryScenarioSelectPopUpComponent, {
      width: '80%',
      height: '80%',
      data: {
        isMultiSelect: false,
        title: 'Select Industry Scenario',
        validFcorgs: [industryFcorg]
      }
    });
    dialogRef.afterClosed().subscribe(res => {
      this.selectedIndustryScenario = res ? res.selectedScenarios[0] : null;
      this.updateStepTwoComplete();
    });
  }

  /**
   * Checks whether there is a selected industry scenario and whether the proper information is selected for the current
   * source and sets completion status
   */
  updateStepTwoComplete(): void {
    let isSharedVolOptionSelected: boolean;
    if (this.selectedIndustryScenario !== null) {
      switch (this.selectedSource.value) {
        case this.fromScenario:
          isSharedVolOptionSelected = this.keepSharedVol !== null && this.keepSharedVol !== undefined;
          this.stepTwoComplete = (this.selectedScenario !== null && this.selectedScenario !== undefined) &&
            (isSharedVolOptionSelected !== this.isTvp);
          break;
        case this.fromPortfolio:
          this.stepTwoComplete = (this.selectedFcorg !== null) || (this.isTvp && this.selectedVehiclePrograms.length !== 0);
          break;
        case this.fromTemplate:
          this.stepTwoComplete = !(this.file === null || this.templateFcorg === null || this.selectedSheet === null);
          break;
      }
    } else {
      this.stepTwoComplete = false;
    }
  }

  /**
   * Sets selections and provided data for each source to its original state
   * @private
   */
  private resetSecondPage(): void {
    this.selectedScenario = null;
    this.keepSharedVol = null;
    this.selectedFcorg = null;
    this.selectedSheet = '';
    this.file = null;
    this.templateFcorg = null;
    this.selectedIndustryScenario = null;
  }

  /**
   * Opens a pop-up to update the scenario name
   * @param field the current name
   * @param maxLength the max length the field can be
   */
  updateScenarioName(field: string, maxLength: number): void {
    this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update Scenario Name',
        currentValueToUpdate: field,
        maxLength,
        hidePreview: true,
        saveButtonText: 'Save',
        showCharCount: true
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.scenarioName = result.data.newValue;
      }
    });
  }

  /**
   * Opens a pop-up to update the scenario assumption
   * @param field the current assumption
   * @param maxLength the max length the field can be
   */
  updateAssumption(field: string, maxLength: number): void {
    this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update Assumption',
        currentValueToUpdate: field,
        maxLength,
        hidePreview: true,
        saveButtonText: 'Save',
        showCharCount: true
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.assm = result.data.newValue;
      }
    });
  }

  /**
   * Shows the errors in the template format in a popup
   * @param info the errors to display to the user
   */
  showTemplateErrors(info: string): void {
    this.dialog.open(InfoPopUpComponent, {
      data: {
        title: 'Template Errors',
        info
      }
    });
  }

  /**
   * Triggers from clicking create button
   * Calls the proper create function based on the selected source
   */
  createScenario(): void {
    if (this.isTvp) {
      this.createScenarioTVP();
    } else {
      switch (this.selectedSource.value) {
        case this.fromScenario:
          this.createScenarioFromScenario(this.scenarioName, this.scenCat, this.scenType, this.assm, this.selectedIndustryScenario.id,
            this.user, this.selectedScenario.fcorg, this.keepSharedVol, this.selectedScenario.vehScenId);
          break;
        case this.fromPortfolio:
          this.createScenarioFromPortfolio(this.scenarioName, this.scenCat, this.scenType, this.assm, this.selectedIndustryScenario.id,
            this.user, this.selectedFcorg);
          break;
        case this.fromTemplate:
          this.createScenarioFromTemplate(this.scenarioName, this.scenCat, this.scenType, this.assm,
            this.selectedIndustryScenario.id, this.user, this.selectedSheet, this.file);
          break;
      }
    }
  }

  /**
   * Calls service function to create a scenario from a base scenario
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related scenario
   * @param userId the gmid of the user
   * @param fcorg the fcorg of the base scenario
   * @param keepSharedVol 1 if keeping shared volume, 0 if keeping GM volume the same
   * @param scenId The id of the base vehicle scenario
   * @private
   */
  private createScenarioFromScenario(name: string, scenCat: string, scenType: string, assm: string,
                                     indScenId: number, userId: string, fcorg: string, keepSharedVol: number, scenId: number): void {
    this.loading.track(this.vehicleScenarioService.createVehicleScenarioFromScenario(name, scenCat, scenType, assm,
      indScenId, userId, fcorg, keepSharedVol, scenId)).subscribe({
      error: (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      },
      complete: () => {
        this.showConfirmationDialog();
      }
      }
    );
  }

  /**
   * Calls service function to create a scenario from portfolio
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param userId the gmid of the user
   * @param fcorg the chosen fcorg
   * @private
   */
  private createScenarioFromPortfolio(name: string, scenCat: string, scenType: string, assm: string,
                                      indScenId: number, userId: string, fcorg: string): void {
    this.loading.track(this.vehicleScenarioService.createVehicleScenarioFromPortfolio(name, scenCat, scenType, assm,
      indScenId, userId, fcorg)).subscribe(
      {
        error: (err) => {
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        complete: () => {
          this.showConfirmationDialog();
        }
      }
    );
  }

  /**
   * Calls service function to create a scenario from a base template
   * @param name the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param userId the gmid of the user
   * @param selectedSheet the sheet with the template information
   * @param file the uploaded Excel file
   * @private
   */
  private createScenarioFromTemplate(name: string, scenCat: string, scenType: string, assm: string,
                                     indScenId: number, userId: string, selectedSheet: string, file: File): void {
    this.loading.track(this.vehicleScenarioService.createVehicleScenarioFromTemplate(name, scenCat, scenType, assm,
      indScenId, userId, selectedSheet, file)).subscribe({
      error: (error) => {
        const info = error.replaceAll('. ', '.\n');
        this.showTemplateErrors(info);
      },
      complete: () => {
        this.showConfirmationDialog();
      }
    });
  }

  /**
   * When creating a TVP scenario, chooses the correct method based on the source
   */
  private createScenarioTVP(): void {
    switch (this.selectedSource.value) {
      case this.fromScenario:
        this.createScenarioFromScenarioTVP(this.scenarioName, this.scenCat, this.scenType, this.assm, this.selectedIndustryScenario.id,
          this.user, this.selectedScenario.fcorg, this.propulsionType.id, this.engProgClass.id, this.forecastState.id,
          this.selectedScenario.vehScenId);
        break;
      case this.fromPortfolio:
        this.createScenarioFromPortfolioTVP(this.scenarioName, this.scenCat, this.scenType, this.assm, this.selectedIndustryScenario.id,
          this.user, 'GMTVP', this.propulsionType.id, this.engProgClass.id, this.forecastState.id,
          this.createStringFromProgramList(this.selectedVehiclePrograms));
        break;
    }
  }

  /**
   * Creates a new TVP vehicle scenario from a base scenario
   * @param scenarioName the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param userId the gmid of the user
   * @param fcorg the fcorg of the base scenario
   * @param propTypeId the id of the related propulsion type
   * @param engProgClassId the id of the related engineering program class
   * @param fcStateId the id of the related forecast state
   * @param scenId the id of the base scenario
   */
  private createScenarioFromScenarioTVP(scenarioName, scenCat, scenType, assm, indScenId, userId, fcorg, propTypeId,
                                        engProgClassId, fcStateId, scenId): void {
    this.loading.track(this.vehicleScenarioService.createVehicleScenarioFromScenarioTVP(scenarioName, scenCat, scenType,
      assm, indScenId, userId, fcorg, propTypeId, engProgClassId, fcStateId, scenId)).subscribe(
      {
        error: (err) => {
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        complete: () => {
          this.showConfirmationDialog();
        }
      }
    );
  }

  /**
   * Creates a new TVP vehicle scenario from a portfolio
   * @param scenarioName the scenario name
   * @param scenCat the scenario category
   * @param scenType the scenario type
   * @param assm the scenario assumption
   * @param indScenId the id of the related industry scenario
   * @param userId the gmid of the user
   * @param fcorg the fcorg of the base scenario
   * @param propTypeId the id of the related propulsion type
   * @param engProgClassId the id of the related engineering program class
   * @param fcStateId the id of the related forecast state
   * @param programList the portion of the query that filters by the selected vehicle programs
   * @private
   */
  private createScenarioFromPortfolioTVP(scenarioName, scenCat, scenType, assm, indScenId, userId, fcorg, propTypeId,
                                         engProgClassId, fcStateId, programList): void {
    this.loading.track(this.vehicleScenarioService.createVehicleScenarioFromPortfolioTVP(scenarioName, scenCat,
      scenType, assm, indScenId, userId, fcorg, propTypeId, engProgClassId, fcStateId, programList)).subscribe(
      {
        error: (err) => {
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        complete: () => {
          this.showConfirmationDialog();
        }
      }
    );
  }

  showConfirmationDialog(): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Scenario Created Successfully',
        message: 'Do you want to view the scenario created?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });

    // If user clicks 'Yes', navigate to home page with the newly created scenario selected
    dialogRef.componentInstance.yes.subscribe(() => {
      this.loading.track(this.vehicleScenarioService.getVehicleScenarioByName(this.stateService.getUserGmid(),
        this.scenarioName)).subscribe(
        data => {
          this.stateService.setSelectedScenario(data[0], true);
          this.stateService.setCompareScenario(data[0]);
        },
        (err) => {
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        () => {
          this.router.navigate(['']);
        }
      );
    });
    // If user clicks 'No', just navigate back to home page without selecting newly created scenario
    dialogRef.componentInstance.no.subscribe(() => {
      this.router.navigate(['']);
    });
  }

  /**
   * Translates the FCORG to it's simplified version
   * @param input the FCORG to translate
   */
  translateFcorg(input: string): string {
    const fcorgMap: { [key: string]: string } = {
      GME: 'GME',
      'GME-LUX': 'GME',
      'GME-NONGM': 'GME',
      'CHEV CAD EUROPE': 'GME',
      'OPEL/VAUXHALL': 'GME',
      GMSA: 'GMSA',
      'GMSA-NONGM': 'GMSA',
      GMI: 'GMI',
      'GMI-NONGM': 'GMI',
      'BUICKCHVCAD ISRAEL': 'GMI',
      GMNA: 'GMNA',
      'GMNA-NONGM': 'GMNA',
      US: 'GMNA',
      GMGC: 'GMGC',
      'GMGC-NONGM': 'GMGC',
    };

    return fcorgMap[input] || input;
  }

  /**
   * creates the part of the sql query to filter results by the vehicle program
   * @param selectedVehiclePrograms a list of vehicle programs to filter the scenario by
   */
  createStringFromProgramList(selectedVehiclePrograms: TvpVehicleProgram[]): string {
    let listString = '';
    const endString = ' OR ';
    for (const item of selectedVehiclePrograms) {
      listString += `(MKTDIV='${
        item.mktDiv}' AND BRAND='${
        item.brand}' AND PRGMCODE='${
        item.prgmCode}' AND STYLECODE='${
        item.styleCode}' AND BODYSTYLE='${
        item.bodyStyle}') OR `;
    }
    return listString.substring(0, listString.length - endString.length);
  }

  protected readonly BUSINESS_PLAN = BUSINESS_PLAN;
  protected readonly TVP = TVP;
}
